import { useTranslation } from '@/hooks/useTranslation';
import { FormEvent, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Edit, Eye, Plus, RefreshCw, Trash2 } from 'lucide-react';
import TVChannelsLayout from '@/pages/content/TVChannelsLayout';
import Button from '@/components/ui/Button';
import DataTable from '@/components/ui/DataTable';
import Input from '@/components/ui/Input';
import Modal from '@/components/ui/Modal';
import { TableColumn } from '@/types';
import { dateWithMonthName } from '@/utils/formatters';
import {
  useCreateIptvProvider,
  useDeleteIptvProvider,
  useIptvProviders,
  useUpdateIptvProvider,
} from '@/hooks/useIptvProviders';
import { IptvProvider } from '@/services/iptvProviderService';

interface ProviderFormState {
  name: string;
  endpoint: string;
  credentialsRequired: boolean;
  proxyEnabled: boolean;
  username: string;
  password: string;
  notes: string;
}

const emptyForm: ProviderFormState = {
  name: '',
  endpoint: '',
  credentialsRequired: true,
  proxyEnabled: false,
  username: '',
  password: '',
  notes: '',
};

const TVChannelsProviders = () => {
  const { t } = useTranslation();
  const proxyLabels = t.iptvProxy;
  const { data: providers = [], isLoading, isError, error, refetch, isFetching } = useIptvProviders();
  const createProviderMutation = useCreateIptvProvider();
  const updateProviderMutation = useUpdateIptvProvider();
  const deleteProviderMutation = useDeleteIptvProvider();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingProvider, setEditingProvider] = useState<IptvProvider | null>(null);
  const [form, setForm] = useState<ProviderFormState>(emptyForm);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const isSubmitting = createProviderMutation.isPending || updateProviderMutation.isPending;

  const sortedProviders = useMemo(
    () => [...providers].sort((a, b) => a.name.localeCompare(b.name)),
    [providers]
  );

  const openCreateModal = () => {
    setEditingProvider(null);
    setForm(emptyForm);
    setSubmitError(null);
    setIsModalOpen(true);
  };

  const openEditModal = (provider: IptvProvider) => {
    setEditingProvider(provider);
    setForm({
      name: provider.name,
      proxyEnabled: provider.proxyEnabled ?? false,
      endpoint: provider.endpoint,
      credentialsRequired: Boolean(provider.username),
      username: provider.username || '',
      password: '',
      notes: provider.notes || '',
    });
    setSubmitError(null);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    if (isSubmitting) {
      return;
    }
    setIsModalOpen(false);
    setEditingProvider(null);
    setForm(emptyForm);
    setSubmitError(null);
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setSubmitError(null);

    try {
      const username = form.credentialsRequired ? form.username : '';
      const password = form.credentialsRequired ? form.password : '';
      const passwordPayload = form.credentialsRequired && !password.trim() ? undefined : password;

      if (editingProvider) {
        await updateProviderMutation.mutateAsync({
          id: editingProvider.id,
          name: form.name,
          endpoint: form.endpoint,
          proxyEnabled: form.proxyEnabled,
          username,
          password: passwordPayload,
          notes: form.notes,
        });
      } else {
        await createProviderMutation.mutateAsync({
          name: form.name,
          endpoint: form.endpoint,
          proxyEnabled: form.proxyEnabled,
          username: username || undefined,
          password: password || undefined,
          notes: form.notes,
        });
      }

      closeModal();
    } catch (e) {
      const message = e instanceof Error ? e.message : 'No se pudo guardar la lista';
      setSubmitError(message);
    }
  };

  const handleDelete = async (provider: IptvProvider) => {
    const confirmed = window.confirm(`¿Eliminar lista ${provider.name}?`);
    if (!confirmed) {
      return;
    }

    try {
      await deleteProviderMutation.mutateAsync(provider.id);
    } catch (e) {
      setSubmitError(e instanceof Error ? e.message : 'No se pudo eliminar la lista');
    }
  };

  const renderConnections = (provider: IptvProvider) => {
    if (provider.activeCons === null || provider.activeCons === undefined || provider.maxConnections === null || provider.maxConnections === undefined) {
      return (
        <span className="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-700">
          N/A
        </span>
      );
    }

    const active = provider.activeCons;
    const max = provider.maxConnections;
    const remainingRatio = max > 0 ? (max - active) / max : 0;

    const badgeClass =
      remainingRatio < 0.2
        ? 'bg-red-100 text-red-800'
        : remainingRatio < 0.3
          ? 'bg-yellow-100 text-yellow-800'
          : 'bg-green-100 text-green-800';

    return (
      <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${badgeClass}`}>
        {`${active}/${max}`}
      </span>
    );
  };

  const renderExpDate = (provider: IptvProvider) => {
    if (provider.expDate === null || provider.expDate === undefined) {
      return 'N/A';
    }

    const date = new Date(provider.expDate * 1000);
    if (Number.isNaN(date.getTime())) {
      return 'N/A';
    }

    return dateWithMonthName(date.toISOString(), 'N/A');
  };

  const columns: TableColumn<IptvProvider>[] = [
    {
      key: 'name',
      header: 'Nombre',
      accessor: (provider) => (
        <div className="font-medium text-gray-900">
          <Link to={`/content/tv-channels/providers/${provider.id}/list`} className="link">
            {provider.name}
          </Link>
        </div>
      ),
    },
    {
      key: 'username',
      header: 'Usuario',
      accessor: (provider) => <span className="text-sm text-gray-900">{provider.username || '-'}</span>,
    },
    {
      key: 'endpoint',
      header: 'Endpoint',
      accessor: (provider) => (
        <span className="block max-w-md truncate text-sm text-gray-900" title={provider.endpoint}>
          {provider.endpoint}
        </span>
      ),
    },
    {
      key: 'proxyEnabled',
      header: proxyLabels.route,
      accessor: (provider) => provider.proxyEnabled ? proxyLabels.proxy : proxyLabels.direct,
    },
    {
      key: 'connections',
      header: 'Conexiones',
      accessor: (provider) => renderConnections(provider),
    },
    {
      key: 'expDate',
      header: 'Expira',
      accessor: (provider) => <span className="text-sm text-gray-900">{renderExpDate(provider)}</span>,
    },
    {
      key: 'actions',
      header: 'Acciones',
      align: 'center',
      accessor: (provider) => (
        <div className="flex items-center justify-center gap-2">
          <Link to={`/content/tv-channels/providers/${provider.id}/list`}>
            <Button size="sm" variant="outline" title="Ver lista">
              <Eye className="h-4 w-4" />
            </Button>
          </Link>
          <Button size="sm" variant="outline" onClick={() => openEditModal(provider)}>
            <Edit className="h-4 w-4" />
          </Button>
          <Button
            size="sm"
            variant="danger"
            onClick={() => handleDelete(provider)}
            disabled={deleteProviderMutation.isPending}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      ),
      className: 'w-44',
    },
  ];

  return (
    <TVChannelsLayout>
      <div className="space-y-4">
        {isError && (
          <div className="rounded-md bg-red-50 p-4 text-sm text-red-700">
            {error instanceof Error ? error.message : 'Error al cargar proveedores'}
          </div>
        )}

        {isLoading ? (
          <div className="border border-gray-200 bg-white px-4 py-8 text-center text-sm text-gray-500 shadow">
            Cargando proveedores...
          </div>
        ) : (
          <DataTable
            data={sortedProviders}
            columns={columns}
            paginate={false}
            tableStyle="compact"
            showToolbar={false}
            className="shadow"
            headerRenderer={() => (
              <div className="border-b border-gray-200 bg-white">
                <div className="flex flex-col gap-3 px-4 py-2 md:flex-row md:items-center md:justify-between">
                  <h3 className="text-lg font-medium text-gray-900">Proveedores de listas</h3>
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => refetch()}
                      disabled={isFetching}
                    >
                      <RefreshCw className={`mr-1 h-4 w-4 ${isFetching ? 'animate-spin' : ''}`} />
                      Refrescar
                    </Button>
                    <Button size="sm" onClick={openCreateModal} className="bg-indigo-600 hover:bg-indigo-700 text-white">
                      <Plus className="mr-1 h-4 w-4" />
                      Nuevo proveedor
                    </Button>
                  </div>
                </div>
              </div>
            )}
          />
        )}

        <Modal
          isOpen={isModalOpen}
          onClose={closeModal}
          title={editingProvider ? 'Editar proveedor' : 'Nuevo proveedor'}
          size="xl"
          closeOnBackdrop={!isSubmitting}
          closeOnEsc={!isSubmitting}
        >
          <form onSubmit={handleSubmit} className="space-y-4 px-2 py-2">
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <Input
                    label="Nombre"
                    value={form.name}
                    onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
                    required
                    disabled={isSubmitting}
                  />
                  <Input
                    label="Endpoint"
                    value={form.endpoint}
                    onChange={(e) => setForm((prev) => ({ ...prev, endpoint: e.target.value }))}
                    placeholder="https://example.com"
                    required
                    disabled={isSubmitting}
                  />
                  <label className="flex items-center gap-2 self-end rounded-md border border-gray-200 px-3 py-2 text-sm text-gray-700">
                    <input
                      type="checkbox"
                      checked={form.credentialsRequired}
                      onChange={(e) => setForm((prev) => ({
                        ...prev,
                        credentialsRequired: e.target.checked,
                        username: e.target.checked ? prev.username : '',
                        password: e.target.checked ? prev.password : '',
                      }))}
                      disabled={isSubmitting}
                      className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                    />
                    Requiere usuario y password
                  </label>
                  <Input
                    label="Usuario"
                    value={form.username}
                    onChange={(e) => setForm((prev) => ({ ...prev, username: e.target.value }))}
                    disabled={isSubmitting || !form.credentialsRequired}
                  />
                  <Input
                    label="Password"
                    type="password"
                    value={form.password}
                    onChange={(e) => setForm((prev) => ({ ...prev, password: e.target.value }))}
                    placeholder={editingProvider ? 'Dejar vacío para mantener la actual' : undefined}
                    disabled={isSubmitting || !form.credentialsRequired}
                  />
                </div>

                <div>
                  <label className="flex items-center gap-2 text-sm text-gray-700">
                    <input
                      type="checkbox"
                      checked={form.proxyEnabled}
                      onChange={(e) => setForm((prev) => ({ ...prev, proxyEnabled: e.target.checked }))}
                      disabled={isSubmitting}
                      className="h-4 w-4 rounded border-gray-300 text-indigo-600"
                    />
                    {proxyLabels.enabled}
                  </label>
                  <p className="mt-1 text-sm text-gray-500">{proxyLabels.help}</p>
                </div>

                <div>
                  <label htmlFor="notes" className="mb-1 block text-sm font-medium text-gray-700">
                    Notas
                  </label>
                  <textarea
                    id="notes"
                    value={form.notes}
                    onChange={(e) => setForm((prev) => ({ ...prev, notes: e.target.value }))}
                    disabled={isSubmitting}
                    rows={4}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                  />
                </div>

                {submitError && (
                  <div className="rounded-md bg-red-50 p-3 text-sm text-red-700">
                    {submitError}
                  </div>
                )}

                <div className="flex justify-end gap-2 border-t border-gray-200 pt-4">
                  <Button type="button" variant="outline" onClick={closeModal} disabled={isSubmitting}>
                    Cancelar
                  </Button>
                  <Button type="submit" className="bg-indigo-600 hover:bg-indigo-700 text-white" disabled={isSubmitting}>
                    {isSubmitting ? 'Guardando...' : 'Guardar'}
                  </Button>
                </div>
          </form>
        </Modal>
      </div>
    </TVChannelsLayout>
  );
};

export default TVChannelsProviders;
